import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { SortingState } from '@tanstack/react-table';
import { usePost } from '../API';
import { logAdminPanelError } from '../utils/adminPanel.utils';

export type MyReviewApplicant = {
  uid: string;
  legal_name: string | null;
  review_state: string | null;
  assigned_at: string | null;
  queue: {
    name: string;
    startSection: string;
  };
};

export type MyReviewsFilters = {
  queue: string[];
  reviewState: string[];
};

export type MyReviewsStats = {
  claimed: number;
  in_progress: number;
  completed: number;
};

type MyReviewsContextType = {
  applicants: Array<MyReviewApplicant>;
  getMyReviews: (page?: number, limit?: number, search?: string, sorting?: SortingState, filters?: MyReviewsFilters, silent?: boolean) => Promise<void>;
  isLoading: boolean;
  isSearching: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  search: string;
  searchDisplay: string;
  sorting: SortingState;
  filters: MyReviewsFilters;
  availableQueues: string[];

  // Stats
  stats: MyReviewsStats | null;
  getMyReviewsStats: (silent?: boolean) => Promise<void>;
  statsLoading: boolean;

  // Pagination callbacks
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onSearchChange: (search: string) => void;
  onSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;
  onFiltersChange: (filters: MyReviewsFilters) => void;
};

type MyReviewsProviderProps = {
  children: React.ReactNode;
};

const MyReviewsContext = createContext<MyReviewsContextType>({
  applicants: [],
  getMyReviews: async () => {},
  isLoading: false,
  isSearching: false,
  total: 0,
  page: 1,
  limit: 25,
  totalPages: 0,
  search: '',
  searchDisplay: '',
  sorting: [],
  filters: { queue: [], reviewState: [] },
  availableQueues: [],

  stats: null,
  getMyReviewsStats: async () => {},
  statsLoading: false,

  onPaginationChange: () => {},
  onSearchChange: () => {},
  onSortingChange: () => {},
  onFiltersChange: () => {},
});

export const useMyReviews = () => {
  const context = useContext(MyReviewsContext);
  if (!context) {
    throw new Error("useMyReviews must be used within a MyReviewsProvider");
  }

  return context;
};

export const MyReviewsProvider: React.FC<MyReviewsProviderProps> = ({ children }) => {
  const [applicants, setApplicants] = useState<Array<MyReviewApplicant>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState(''); // For API calls
  const [searchDisplay, setSearchDisplay] = useState(''); // For UI display
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<MyReviewsFilters>({ queue: [], reviewState: [] });
  const [availableQueues, setAvailableQueues] = useState<string[]>([]);
  const [stats, setStats] = useState<MyReviewsStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getMyReviewsPost = usePost("/api/admin_panel/my_reviews");
  const getMyReviewsStatsPost = usePost("/api/admin_panel/my_reviews_stats");

  const getMyReviews = useCallback(async (
    pageParam?: number,
    limitParam?: number,
    searchParam?: string,
    sortingParam?: SortingState,
    filtersParam?: MyReviewsFilters,
    silent?: boolean
  ) => {
    const isSearchRequest = searchParam !== undefined && searchParam !== search;

    if (!silent) {
      if (isSearchRequest) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }
    }

    const response = await getMyReviewsPost({
      page: pageParam ?? page,
      limit: limitParam ?? limit,
      search: searchParam ?? search,
      sorting: sortingParam ?? sorting,
      queue: filtersParam?.queue ?? filters.queue,
      reviewState: filtersParam?.reviewState ?? filters.reviewState
    });

    if (response.error || !response.data) {
      if (response.error) {
        logAdminPanelError(response.error, 'Failed to fetch my reviews');
      }
      if (!silent) {
        setIsLoading(false);
        setIsSearching(false);
      }
      return;
    }

    setApplicants(response.data.applicants || []);
    setTotal(response.data.total || 0);
    setTotalPages(Math.ceil((response.data.total || 0) / (limitParam ?? limit)));
    setAvailableQueues(response.data.availableQueues || []);

    if (pageParam !== undefined) setPage(pageParam);
    if (limitParam !== undefined) setLimit(limitParam);
    if (searchParam !== undefined) {
      setSearch(searchParam);
      setSearchDisplay(searchParam);
    }
    if (sortingParam !== undefined) setSorting(sortingParam);
    if (filtersParam !== undefined) setFilters(filtersParam);

    if (!silent) {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [getMyReviewsPost, page, limit, search, sorting, filters]);

  const onPaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    const newPage = pagination.pageIndex + 1; // Convert from 0-based to 1-based
    const newLimit = pagination.pageSize;

    setPage(newPage);
    setLimit(newLimit);
    getMyReviews(newPage, newLimit, search, sorting, filters);
  }, [getMyReviews, search, sorting, filters]);

  const onSearchChange = useCallback((searchValue: string) => {
    setSearchDisplay(searchValue);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1); // Reset to first page on search
      getMyReviews(1, limit, searchValue, sorting, filters);
    }, 300);
  }, [getMyReviews, limit, sorting, filters]);

  const onSortingChange = useCallback((newSorting: React.SetStateAction<SortingState>) => {
    const sortingValue = typeof newSorting === 'function' ? newSorting(sorting) : newSorting;
    setSorting(sortingValue);
    getMyReviews(page, limit, search, sortingValue, filters);
  }, [getMyReviews, page, limit, search, sorting, filters]);

  const onFiltersChange = useCallback((newFilters: MyReviewsFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
    getMyReviews(1, limit, search, sorting, newFilters);
  }, [getMyReviews, limit, search, sorting]);

  const getMyReviewsStats = useCallback(async (silent?: boolean) => {
    if (!silent) {
      setStatsLoading(true);
    }

    const response = await getMyReviewsStatsPost({});

    if (response.error || !response.data) {
      if (response.error) {
        logAdminPanelError(response.error, 'Failed to fetch my reviews stats');
      }
      if (!silent) {
        setStatsLoading(false);
      }
      return;
    }

    setStats(response.data);
    if (!silent) {
      setStatsLoading(false);
    }
  }, [getMyReviewsStatsPost]);

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    applicants,
    getMyReviews,
    isLoading,
    isSearching,
    total,
    page,
    limit,
    totalPages,
    search,
    searchDisplay,
    sorting,
    filters,
    availableQueues,
    stats,
    getMyReviewsStats,
    statsLoading,
    onPaginationChange,
    onSearchChange,
    onSortingChange,
    onFiltersChange,
  };

  return (
    <MyReviewsContext.Provider value={value}>
      {children}
    </MyReviewsContext.Provider>
  );
};
